import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Mail,
  MessageSquare,
  CheckCircle2,
  QrCode,
} from 'lucide-react'
import type { Contact as AppContact, SelectedContact } from '@/routes/_public/app'
import QRCode from 'react-qr-code'

interface SendFlowProps {
  contacts: SelectedContact[]
  message: string
  smsMessage: string
  subject: string
  signature: string
  onStartOver: () => void
  onSendAnother: () => void
}

export function SendFlow({
  contacts,
  message,
  smsMessage,
  subject,
  signature,
  onStartOver,
  onSendAnother,
}: SendFlowProps) {
  const [sentIds, setSentIds] = useState<Set<string>>(new Set())
  const [isMobile, setIsMobile] = useState(true)
  const [showQR, setShowQR] = useState<string | null>(null)

  useEffect(() => {
    // Detect if user is on mobile device
    const checkMobile = () => {
      const mobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
      setIsMobile(mobile)
    }
    checkMobile()
  }, [])

  type SentChannel = 'sms' | 'email' | 'email-gmail'

  const markAsSent = (id: string, channel: SentChannel) => {
    setSentIds((prev) => new Set([...prev, `${id}:${channel}`]))
  }

  const unmarkAsSent = (id: string, channel: SentChannel) => {
    setSentIds((prev) => {
      const next = new Set(prev)
      next.delete(`${id}:${channel}`)
      return next
    })
  }

  const generateSMSLink = (contact: AppContact): string => {
    const personalizedMessage = (smsMessage || message).replace(
      /\{\{firstName\}\}/g,
      getFirstName(contact.name),
    )
    const encodedMessage = encodeURIComponent(personalizedMessage)

    // iOS uses & separator, Android uses ? separator
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent)
    const separator = isIOS ? '&' : '?'

    return `sms:${contact.phone}${separator}body=${encodedMessage}`
  }

  const generateEmailLink = (contact: AppContact): string => {
    const emailBody = appendSignature(message, signature)
    const personalizedMessage = emailBody.replace(
      /\{\{firstName\}\}/g,
      getFirstName(contact.name),
    )
    const encodedMessage = encodeURIComponent(personalizedMessage)
    const encodedSubject = encodeURIComponent(
      (subject || 'Message from MessageDrafts').replace(
        /\{\{firstName\}\}/g,
        getFirstName(contact.name),
      ),
    )

    return `mailto:${contact.email}?subject=${encodedSubject}&body=${encodedMessage}`
  }

  const generateGmailLink = (contact: AppContact): string => {
    const emailBody = appendSignature(message, signature)
    const personalizedMessage = emailBody.replace(
      /\{\{firstName\}\}/g,
      getFirstName(contact.name),
    )
    const encodedMessage = encodeURIComponent(personalizedMessage)
    const encodedSubject = encodeURIComponent(
      (subject || 'Message from MessageDrafts').replace(
        /\{\{firstName\}\}/g,
        getFirstName(contact.name),
      ),
    )
    const encodedTo = encodeURIComponent(contact.email ?? '')
    return `https://mail.google.com/mail/?view=cm&fs=1&to=${encodedTo}&su=${encodedSubject}&body=${encodedMessage}`
  }

  const handleSendClick = (
    contact: SelectedContact,
    link: string,
    channel: SentChannel,
  ) => {
    if (isMobile) {
      window.location.href = link
      markAsSent(contact.id, channel)
    } else {
      setShowQR(link)
    }
  }

  const smsContacts = contacts.filter((c) => c.sendSms && c.phone)
  const emailContacts = contacts.filter((c) => c.sendEmail && c.email)

  const totalSent =
    smsContacts.filter((c) => sentIds.has(`${c.id}:sms`)).length +
    emailContacts.filter(
      (c) =>
        sentIds.has(`${c.id}:email`) || sentIds.has(`${c.id}:email-gmail`),
    ).length
  const totalContacts = smsContacts.length + emailContacts.length
  const progress = totalContacts ? (totalSent / totalContacts) * 100 : 0

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold">Send Your Messages</h1>
        <p className="text-slate-600">
          {isMobile
            ? 'Tap each contact to open a personalized draft.'
            : 'Click to open a personalized draft, or scan a QR code with your phone.'}
        </p>
      </div>

      {/* Progress */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="font-semibold">Progress</span>
              <span className="text-slate-600">
                {totalSent} of {totalContacts} sent
              </span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div
                className="bg-slate-900 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Trust Message */}
      {!isMobile && (
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg text-sm text-blue-900">
          <p className="font-semibold mb-1">Desktop Mode Detected</p>
          <p>
            Click "Open Text Draft", "Open Email Draft", or "Open Draft with
            Gmail" to use your computer's messaging app. Alternatively, scan
            the QR codes with your phone's camera to open message drafts there.
          </p>
        </div>
      )}

      <div className="bg-slate-50 border border-slate-200 p-4 rounded-lg text-sm text-slate-700">
        <p className="font-semibold mb-1">Remember:</p>
        <p>
          Messages open as drafts in your phone or email app.{' '}
          <strong>Nothing is sent automatically.</strong> You must tap Send in
          each app.
        </p>
      </div>

      {/* SMS Contacts */}
      {smsContacts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Text Messages ({smsContacts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {smsContacts.map((contact) => {
              const link = generateSMSLink(contact)

              return (
                <div
                  key={contact.id}
                  className="flex items-center gap-3 p-3 border rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold">{contact.name}</p>
                    <p className="text-sm text-slate-600">{contact.phone}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {sentIds.has(`${contact.id}:sms`) ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="flex items-center gap-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                        onClick={() => unmarkAsSent(contact.id, 'sms')}
                        aria-label="Show open draft buttons again"
                      >
                        <CheckCircle2 className="h-5 w-5" />
                        <span className="text-sm font-semibold">Opened</span>
                      </Button>
                    ) : isMobile ? (
                      <Button
                        onClick={() => handleSendClick(contact, link, 'sms')}
                        size="sm"
                      >
                        Text Draft
                      </Button>
                    ) : (
                      <Button
                        onClick={() => {
                          window.location.href = link
                          markAsSent(contact.id, 'sms')
                        }}
                        size="sm"
                      >
                        Open Text Draft
                      </Button>
                    )}
                    {!isMobile && (
                      <Button
                        onClick={() => setShowQR(link)}
                        size="sm"
                        variant="outline"
                      >
                        Show QR
                      </Button>
                    )}
                  </div>
                </div>
              )
            })}
          </CardContent>
        </Card>
      )}

      {/* Email Contacts */}
      {emailContacts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Email Messages ({emailContacts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {emailContacts.map((contact) => {
              const link = generateEmailLink(contact)
              const mailtoOpened = sentIds.has(`${contact.id}:email`)
              const gmailOpened = sentIds.has(`${contact.id}:email-gmail`)

              return (
                <div
                  key={contact.id}
                  className="flex items-center gap-3 p-3 border rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold">{contact.name}</p>
                    <p className="text-sm text-slate-600 truncate">
                      {contact.email}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {mailtoOpened ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="flex items-center gap-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                        onClick={() => unmarkAsSent(contact.id, 'email')}
                        aria-label="Show open draft buttons again"
                      >
                        <CheckCircle2 className="h-5 w-5" />
                        <span className="text-sm font-semibold">Opened</span>
                      </Button>
                    ) : isMobile ? (
                      <Button
                        onClick={() => handleSendClick(contact, link, 'email')}
                        size="sm"
                      >
                        Email Draft
                      </Button>
                    ) : (
                      <Button
                        onClick={() => {
                          window.location.href = link
                          markAsSent(contact.id, 'email')
                        }}
                        size="sm"
                      >
                        Open Email Draft
                      </Button>
                    )}
                    {gmailOpened ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="flex items-center gap-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                        onClick={() => unmarkAsSent(contact.id, 'email-gmail')}
                        aria-label="Show open draft buttons again"
                      >
                        <CheckCircle2 className="h-5 w-5" />
                        <span className="text-sm font-semibold">Opened</span>
                      </Button>
                    ) : (
                      <Button
                        onClick={() => {
                          window.open(generateGmailLink(contact), '_blank')
                          markAsSent(contact.id, 'email-gmail')
                        }}
                        size="sm"
                        variant="outline"
                      >
                        Open Draft with Gmail
                      </Button>
                    )}
                    {!isMobile && (
                      <Button
                        onClick={() => setShowQR(link)}
                        size="sm"
                        variant="outline"
                      >
                        Show QR
                      </Button>
                    )}
                  </div>
                </div>
              )
            })}
          </CardContent>
        </Card>
      )}

      {/* QR Code Modal */}
      {showQR && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          onClick={() => setShowQR(null)}
        >
          <Card
            className="max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <QrCode className="h-5 w-5" />
                Scan with Your Phone
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-white p-4 rounded-lg flex justify-center">
                <div className="relative inline-block">
                  <QRCode
                    value={showQR}
                    size={256}
                    level="H"
                    className="block"
                  />
                  <div
                    className="absolute inset-0 flex items-center justify-center pointer-events-none"
                    aria-hidden
                  >
                    <div className="bg-white rounded-lg shadow-sm flex items-center justify-center w-14 h-14">
                      <MessageSquare className="h-7 w-7 text-slate-900" />
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-slate-600 text-center">
                Open your phone's camera and point it at the QR code to open the
                message draft.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    void navigator.clipboard.writeText(showQR)
                    alert('Link copied to clipboard!')
                  }}
                >
                  Copy Link
                </Button>
                <Button className="flex-1" onClick={() => setShowQR(null)}>
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-4">
        <Button variant="outline" onClick={onStartOver} className="flex-1">
          Start Over
        </Button>
        {totalSent === totalContacts && (
          <Button className="flex-1" onClick={onSendAnother}>
            Send Another Message
          </Button>
        )}
      </div>
    </div>
  )
}

function getFirstName(fullName: string): string {
  return fullName.split(' ')[0] || fullName
}

function appendSignature(messageBody: string, signatureText: string): string {
  if (!signatureText.trim()) return messageBody
  const trimmedBody = messageBody.trimEnd()
  const trimmedSignature = signatureText.trim()
  const separator = trimmedBody ? '\n\n' : ''
  return `${trimmedBody}${separator}${trimmedSignature}`.trimEnd()
}
